import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DATE_FORMATS, MatOption } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { HasPermissionDirective, PermissionService } from '@ipg/ngx-frontend-toolset';
import { BaseDialog } from '../../../../components/dialog/base-dialog';

import { SelectionModel } from '@angular/cdk/collections';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import {
  addDays,
  addMilliseconds,
  addYears,
  getDay,
  getHours,
  getMilliseconds,
  getMinutes,
  getSeconds,
  secondsToMilliseconds,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
} from 'date-fns';
import { flow, isFunction, isNil, isNull } from 'lodash-es';
import { Frequency, RRule } from 'rrule';
import {
  catchError,
  combineLatest,
  filter,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { ConfirmHandler } from '../../../../components/dialog/dialog.model';
import { InteractionSelectionDialogService } from '../../../../components/interaction-selection-dialog/services/interaction-selection-dialog.service';
import { MediaPickerDialogService } from '../../../../components/media-picker-dialog/services/media-picker-dialog.service';
import { Interaction } from '../../../../models/interactions.model';
import { MediaItem } from '../../../../models/media.model';
import { permissionLabel } from '../../../../models/permission.model';

import { merge as _merge } from 'lodash-es';
import { ceilNearest } from '../../../../utils/math';
import {
  EventDialogData,
  EventDialogMode,
  SchedulerEventType,
} from '../../models/event-dialog.model';

type EventRecurrence = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
type MonthlyMode = 'bymonthday' | 'byweekday';

const MIN_EVENT_DURATION = 3e5;

const WEEKDAY_NAMES_NL = [
  'zondag',
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag',
];

const ORDINAL_NL = ['1e', '2e', '3e', '4e', '5e'];

function getWeekdayOccurrenceInMonth(date: Date): number {
  return Math.ceil(date.getDate() / 7);
}

@Component({
  selector: 'app-event-dialog',
  imports: [
    MatButton,
    MatDatepickerModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatRadioButton,
    MatRadioGroup,
    MatSelect,
    MatOption,
    MatTooltip,
    MatTimepickerModule,
    MatProgressSpinner,
    ReactiveFormsModule,
    HasPermissionDirective,
    MatSuffix,
  ],
  templateUrl: './event-dialog.component.html',
  styleUrl: './event-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'LL',
          timeInput: 'p',
        },
        display: {
          dateInput: 'eeee, d MMMM yyyy',
          dateA11yLabel: 'LL',
          monthYearLabel: 'yyyy',
          monthYearA11yLabel: 'yyyy',
          timeInput: 'p',
          timeOptionLabel: 'p',
        },
      },
    },
  ],
})
export class EventDialogComponent
  extends BaseDialog<
    EventDialogData & { onConfirm?: ConfirmHandler; mode: EventDialogMode }
  >
  implements OnInit
{
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<EventDialogComponent>>(MatDialogRef);
  private readonly _location = inject(Location);
  private readonly _interactionSelection = inject(InteractionSelectionDialogService);
  private readonly _mediaPicker = inject(MediaPickerDialogService);
  private readonly _permissionService = inject(PermissionService);

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);
  protected readonly creating = signal(false);

  protected readonly createInteractionHref = this._location.prepareExternalUrl(
    this._clientId ? `clients/${this._clientId}/interactions/new` : 'templates/new'
  );

  protected markedForDeletion = false;

  protected readonly now = new Date();

  protected readonly selectedInteraction = signal<Interaction | null>(null);
  protected readonly selectedMedia = signal<MediaItem | null>(null);
  protected readonly eventType = signal<SchedulerEventType>('interaction');

  protected readonly isMediaEvent = computed(
    () => this.eventType() === 'media-image' || this.eventType() === 'media-audio'
  );

  protected readonly canUseMedia = computed(() =>
    this._permissionService.hasPermission('media:read')
  );

  /** Choosing an interaction lists them, which `client_event:*` does not cover */
  protected readonly canReadInteractions = computed(() =>
    this._permissionService.hasPermission('interaction:read')
  );
  protected readonly interactionReadLabel = permissionLabel('interaction:read');

  protected readonly isTemplate = computed(
    () => this.selectedInteraction()?.clientId === null
  );

  protected readonly isTemplateDuplicate = computed(() => {
    const interaction = this.selectedInteraction();

    if (!interaction || interaction.clientId !== null) return false;

    return (interaction.clientAttachmentCount ?? 0) > 0;
  });

  protected readonly form = this._fb.group({
    start: this._fb.control<Date>(new Date(), { validators: [Validators.required] }),
    startTime: this._fb.control<Date>(new Date(), {
      validators: [Validators.required],
    }),
    endTime: this._fb.control<Date>(new Date(), { validators: [Validators.required] }),

    recurrence: this._fb.group({
      freq: this._fb.control<Frequency>(Frequency.DAILY),
      interval: this._fb.control<number>(1),
      byweekday: this._fb.control<number[]>([]),
      bysetpos: this._fb.control<number | null>(null),
      bymonthday: this._fb.control<number | null>(null),
      until: this._fb.control<Date>(new Date()),
    }),
  });

  protected readonly recurrence = this._fb.control<EventRecurrence>('none');
  protected readonly monthlyMode = this._fb.control<MonthlyMode>('bymonthday');
  protected readonly intervalValue = signal(1);

  protected readonly recurrenceEnd = this._fb.control<'never' | 'on' | 'after'>(
    'never'
  );

  protected readonly monthlyByMonthdayLabel = signal('');
  protected readonly monthlyByWeekdayLabel = signal('');

  private readonly _startTimeValue = toSignal(
    this.form.controls.startTime.valueChanges,
    { initialValue: this.form.controls.startTime.value }
  );

  protected readonly minEndTime = computed(() => {
    const interaction = this.selectedInteraction();
    const startTime = this._startTimeValue();

    if (isNull(interaction)) return startTime;

    return this._calculateEndTime(
      startTime,
      secondsToMilliseconds(interaction.estimatedDuration)
    );
  });

  protected readonly defaultRecurrenceOptions: {
    label: string;
    value: EventRecurrence;
  }[] = [
    {
      label: 'Niet herhaald',
      value: 'none',
    },
    {
      label: 'Dagelijks',
      value: 'daily',
    },
    {
      label: 'Wekelijks',
      value: 'weekly',
    },
    {
      label: 'Maandelijks',
      value: 'monthly',
    },
    {
      label: 'Jaarlijks',
      value: 'yearly',
    },
  ];

  private readonly _recurrenceValue = toSignal(this.recurrence.valueChanges, {
    initialValue: this.recurrence.value,
  });

  protected readonly intervalLabel = computed(() => {
    const rec = this._recurrenceValue();
    switch (rec) {
      case 'daily':
        return this.intervalValue() === 1 ? 'dag' : 'dagen';
      case 'weekly':
        return this.intervalValue() === 1 ? 'week' : 'weken';
      case 'monthly':
        return this.intervalValue() === 1 ? 'maand' : 'maanden';
      case 'yearly':
        return this.intervalValue() === 1 ? 'jaar' : 'jaren';
      default:
        return '';
    }
  });

  protected readonly weekDays = [
    {
      shortName: 'm',
      longName: 'maandag',
      value: RRule.MO.weekday,
    },
    {
      shortName: 'd',
      longName: 'dinsdag',
      value: RRule.TU.weekday,
    },
    {
      shortName: 'w',
      longName: 'woensdag',
      value: RRule.WE.weekday,
    },
    {
      shortName: 'd',
      longName: 'donderdag',
      value: RRule.TH.weekday,
    },
    {
      shortName: 'v',
      longName: 'vrijdag',
      value: RRule.FR.weekday,
    },
    {
      shortName: 'z',
      longName: 'zaterdag',
      value: RRule.SA.weekday,
    },
    {
      shortName: 'z',
      longName: 'zondag',
      value: RRule.SU.weekday,
    },
  ];

  protected readonly selectedWeekdays = new SelectionModel<number>(true, []);

  protected readonly startChange$ = this.controls.start.valueChanges;

  protected get controls() {
    return this.form.controls;
  }

  protected get recurrenceControls() {
    return this.controls.recurrence.controls;
  }

  private get _clientId() {
    return this.data.clientId;
  }

  protected readonly isViewMode = this.data.mode === 'view';

  constructor() {
    super();

    this.initializeForm();

    if (this.isViewMode) {
      this.form.disable();
      this.recurrence.disable();
    }
  }

  ngOnInit(): void {
    this.controls.startTime.valueChanges
      .pipe(tap((value) => this.handleStartTimeChange(value)))
      .subscribe();

    this.recurrenceControls.freq.valueChanges
      .pipe(
        filter((value) => value === Frequency.WEEKLY),
        switchMap(() => this.startChange$.pipe(startWith(this.controls.start.value))),
        tap((value) => {
          const weekday = getDay(value);
          const adjustedWeekday = weekday === 0 ? 6 : weekday - 1;

          this.selectedWeekdays.clear();
          this.selectedWeekdays.select(adjustedWeekday);
        })
      )
      .subscribe();

    combineLatest([
      this.recurrence.valueChanges,
      this.startChange$.pipe(startWith(this.controls.start.value)),
    ])
      .pipe(
        tap(([recurrence, start]) =>
          this.handleRecurrenceSelectionChange(recurrence, start)
        )
      )
      .subscribe();

    this.selectedWeekdays.changed
      .pipe(
        tap((selection) => {
          this.recurrenceControls.byweekday.setValue(selection.source.selected.sort());
        })
      )
      .subscribe();

    this.monthlyMode.valueChanges
      .pipe(tap((mode) => this._applyMonthlyMode(mode, this.controls.start.value)))
      .subscribe();
  }

  initializeForm() {
    this.controls.start.setValue(this.data.start);
    this.controls.startTime.setValue(this.data.start);
    this.controls.endTime.setValue(this.data.end);

    this._updateMonthlyLabels(this.data.start);

    if (this.data.interaction) {
      this.selectedInteraction.set(this.data.interaction);

      // For media events the wrapper interaction's type tells us the
      // media kind.  Sync the eventType signal so isMediaEvent()
      // returns true and the flow button is hidden.
      const iType = this.data.interaction.type;
      if (iType === 'media-image' || iType === 'media-audio') {
        this.eventType.set(iType);

        // Build a minimal MediaItem so the view/edit dialog shows the
        // media label instead of "Geen".
        this.selectedMedia.set({
          id: '',
          label: this.data.interaction.label,
          fileId: '',
          fileUrl: '',
          type: iType === 'media-image' ? 'image' : 'audio',
          shareId: null,
          scope: 'client',
        });
      }
    }

    if (this.data.recurrence) {
      const recurrenceMap: Record<number, EventRecurrence> = {
        [Frequency.DAILY]: 'daily',
        [Frequency.WEEKLY]: 'weekly',
        [Frequency.MONTHLY]: 'monthly',
        [Frequency.YEARLY]: 'yearly',
      };

      const { freq, byweekday, interval, bysetpos, bymonthday } = this.data.recurrence;

      const recurrence = recurrenceMap[freq];

      if (byweekday.length > 0) {
        this.selectedWeekdays.select(...byweekday);
      }

      if (freq === Frequency.MONTHLY) {
        if (bymonthday != null) {
          this.monthlyMode.setValue('bymonthday');
        } else if (bysetpos != null) {
          this.monthlyMode.setValue('byweekday');
        }
      }

      this.intervalValue.set(interval ?? 1);

      this.recurrence.setValue(recurrence);
      this.controls.recurrence.patchValue({
        freq,
        interval: interval ?? 1,
        byweekday,
        bysetpos: bysetpos ?? null,
        bymonthday: bymonthday ?? null,
        until: this.data.recurrence.until,
      });
    } else {
      const weekday = getDay(this.data.start);
      const adjustedWeekday = weekday === 0 ? 6 : weekday - 1;
      const until = startOfDay(this.getMaxUntilDate());

      this.selectedWeekdays.select(adjustedWeekday);

      this.controls.recurrence.patchValue({
        until,
      });
    }
  }

  getMaxUntilDate() {
    return addYears(this.controls.start.value, 2);
  }

  handleMonthlyRecurrenceWeekdayChange(event: MatSelectChange) {
    this.selectedWeekdays.clear();
    this.selectedWeekdays.select(event.value);
  }

  handleIntervalChange(value: number) {
    const clamped = Math.max(1, Math.min(value, 99));
    this.intervalValue.set(clamped);
    this.recurrenceControls.interval.setValue(clamped);
  }

  handleRecurrenceSelectionChange(recurrence: EventRecurrence, start: Date) {
    const weekday = getDay(start);
    const adjustedWeekday = weekday === 0 ? 6 : weekday - 1;

    this._updateMonthlyLabels(start);

    // Preserve the existing until date when switching recurrence types
    // (e.g. daily → weekly). Only fall back to the 2-year default when
    // there is no previous value (i.e. creating a brand-new event).
    const currentUntil = this.recurrenceControls?.until?.value;
    const until = isNil(currentUntil)
      ? startOfDay(this.getMaxUntilDate())
      : currentUntil;

    switch (recurrence) {
      case 'daily':
        this.controls.recurrence.patchValue({
          freq: Frequency.DAILY,
          interval: this.intervalValue(),
          byweekday: [],
          bysetpos: null,
          bymonthday: null,
          until,
        });
        break;

      case 'weekly':
        this.controls.recurrence.patchValue({
          freq: Frequency.WEEKLY,
          interval: this.intervalValue(),
          byweekday: [adjustedWeekday],
          bysetpos: null,
          bymonthday: null,
          until,
        });
        break;

      case 'monthly':
        this.controls.recurrence.patchValue({
          freq: Frequency.MONTHLY,
          interval: this.intervalValue(),
          until,
        });
        this._applyMonthlyMode(this.monthlyMode.value, start);
        break;

      case 'yearly':
        this.controls.recurrence.patchValue({
          freq: Frequency.YEARLY,
          interval: this.intervalValue(),
          byweekday: [],
          bysetpos: null,
          bymonthday: null,
          until,
        });
        break;
    }
  }

  private _applyMonthlyMode(mode: MonthlyMode, start: Date) {
    const weekday = getDay(start);
    const adjustedWeekday = weekday === 0 ? 6 : weekday - 1;
    const dayOfMonth = start.getDate();
    const weekdayOccurrence = getWeekdayOccurrenceInMonth(start);

    if (mode === 'bymonthday') {
      this.controls.recurrence.patchValue({
        byweekday: [],
        bysetpos: null,
        bymonthday: dayOfMonth,
      });
      this.selectedWeekdays.clear();
    } else {
      this.controls.recurrence.patchValue({
        byweekday: [adjustedWeekday],
        bysetpos: weekdayOccurrence,
        bymonthday: null,
      });
      this.selectedWeekdays.clear();
      this.selectedWeekdays.select(adjustedWeekday);
    }
  }

  private _updateMonthlyLabels(start: Date) {
    const dayOfMonth = start.getDate();
    const weekday = getDay(start);
    const weekdayOccurrence = getWeekdayOccurrenceInMonth(start);
    const ordinal = ORDINAL_NL[weekdayOccurrence - 1] ?? `${weekdayOccurrence}e`;
    const weekdayName = WEEKDAY_NAMES_NL[weekday];

    this.monthlyByMonthdayLabel.set(`Op dag ${dayOfMonth}`);
    this.monthlyByWeekdayLabel.set(`Op de ${ordinal} ${weekdayName}`);
  }

  handleInteractionSelectionDialog() {
    this._interactionSelection.open(
      (result) =>
        of(result).pipe(
          map((items) => items?.[0]),
          filter((item) => !isNil(item)),
          tap((item) => {
            const currentStart = this.controls.startTime.value;
            const newEndTime = this._calculateEndTime(
              currentStart,
              secondsToMilliseconds(item.estimatedDuration)
            );
            const oldEndTime = this.controls.endTime.value;

            if (newEndTime > oldEndTime) {
              this.controls.endTime.setValue(newEndTime);
            }
            this.selectedInteraction.set(item);
          })
        ),
      {
        multiple: false,
        clientId: this._clientId,
        status: ['published'],
        mode: 'schedule',
        includeShared: true,
        allowCreate: true,
        storageKey: 'scheduler-interaction-selection',
      }
    );
  }

  handleEventTypeChange(type: SchedulerEventType) {
    this.eventType.set(type);
    // Clear selections when switching type
    this.selectedInteraction.set(null);
    this.selectedMedia.set(null);
  }

  handleMediaSelectionDialog() {
    const mediaType = this.eventType() === 'media-image' ? 'image' : 'audio';

    this._mediaPicker.open(
      (result) =>
        of(result as MediaItem).pipe(
          filter((item) => !isNil(item)),
          tap((item) => {
            this.selectedMedia.set(item);

            if (mediaType === 'audio' && item.duration) {
              // Auto-compute end time based on audio duration (rounded to 5 min)
              const currentStart = this.controls.startTime.value;
              const newEndTime = this._calculateEndTime(
                currentStart,
                secondsToMilliseconds(item.duration)
              );
              this.controls.endTime.setValue(newEndTime);
            }
          })
        ),
      {
        type: mediaType,
        context: { scope: 'client', clientId: this._clientId },
      }
    );
  }

  handleStartTimeChange(date: Date) {
    const interaction = this.selectedInteraction();
    const duration = isNull(interaction)
      ? MIN_EVENT_DURATION
      : secondsToMilliseconds(interaction.estimatedDuration);

    const newEnd = this._calculateEndTime(date, duration);
    this.controls.endTime.setValue(newEnd);
  }

  handleEndTimeChange(date: Date) {
    const currentStart = this.controls.startTime.value;
    const interaction = this.selectedInteraction();

    if (!isNull(interaction)) {
      const minEnd = this._calculateEndTime(
        currentStart,
        secondsToMilliseconds(interaction.estimatedDuration)
      );

      if (date < minEnd) {
        this.controls.endTime.setValue(minEnd, { emitEvent: false });
      }
    }
  }

  private _combineDateAndTime(datePart: Date, timePart: Date): Date {
    return flow(
      (d) => setHours(d, getHours(timePart)),
      (d) => setMinutes(d, getMinutes(timePart)),
      (d) => setSeconds(d, getSeconds(timePart)),
      (d) => setMilliseconds(d, getMilliseconds(timePart))
    )(datePart);
  }

  private roundToNearest5Minutes(durationInMs: number) {
    return Math.max(ceilNearest(durationInMs, MIN_EVENT_DURATION), MIN_EVENT_DURATION);
  }

  private _calculateEndTime(start: Date, durationInMs: number) {
    const nearest = this.roundToNearest5Minutes(durationInMs);

    return addMilliseconds(start, nearest);
  }

  protected openFlowView(): void {
    const interaction = this.selectedInteraction();
    if (!interaction) return;
    const path = interaction.clientId === null ? 'templates' : 'interactions';
    window.open(
      this._location.prepareExternalUrl(`${path}/${interaction._id}/flow`),
      '_blank'
    );
  }

  protected handleCreateInteraction(event: Event): void {
    event.preventDefault();
    if (this.creating()) return;

    this.creating.set(true);

    const path = this._clientId
      ? `clients/${this._clientId}/interactions/new`
      : 'templates/new';
    window.location.href = this._location.prepareExternalUrl(path);
  }

  markForDeletionAndConfirm() {
    this.markedForDeletion = true;
    this.handleConfirm();
  }

  override handleConfirm(): void {
    const isMedia = this.isMediaEvent();

    if (isMedia && !this.selectedMedia()) {
      return;
    }
    if (!isMedia && !this.selectedInteraction()) {
      return;
    }

    if (this.form.invalid) {
      return;
    }

    if (
      !this.form.value.start ||
      !this.form.value.startTime ||
      !this.form.value.endTime
    ) {
      return;
    }

    let recurrence = null;

    if (this.recurrence.value !== 'none') {
      const recurrenceFormValue = this.form.value.recurrence as {
        freq: number;
        interval: number;
        byweekday: number[];
        bysetpos: number | null;
        bymonthday: number | null;
        until: Date;
      };

      // Enforce mutual exclusivity: when bymonthday is set (monthly on day X),
      // clear byweekday/bysetpos to prevent "27th that is also a Friday" semantics.
      const hasMonthday = recurrenceFormValue.bymonthday != null;

      recurrence = {
        freq: recurrenceFormValue.freq,
        interval: recurrenceFormValue.interval,
        byweekday: hasMonthday ? [] : recurrenceFormValue.byweekday,
        bysetpos: hasMonthday ? null : recurrenceFormValue.bysetpos,
        bymonthday: recurrenceFormValue.bymonthday,
        until: addDays(recurrenceFormValue.until, 1),
      };
    }

    const startDate = this._combineDateAndTime(
      this.form.value.start,
      this.form.value.startTime
    );
    const endDate = this._combineDateAndTime(
      this.form.value.start,
      this.form.value.endTime
    );

    let result: Record<string, unknown>;

    if (isMedia) {
      result = {
        startDate,
        endDate,
        recurrence,
        eventType: this.eventType(),
        mediaId: this.selectedMedia()!.id,
      };
    } else {
      result = {
        startDate,
        endDate,
        recurrence,
        eventType: 'interaction' as const,
        interaction: this.selectedInteraction(),
      };
    }

    if (this.data.mode === 'edit') {
      result = _merge(result, {
        delete: this.markedForDeletion,
      });
    }

    this.submitted.set(true);

    if (this.data.onConfirm && isFunction(this.data.onConfirm)) {
      const delay = 1000;
      const callback$ = this.data.onConfirm(result);
      const source$ = callback$.pipe(
        shareReplay(1),
        catchError((error) => {
          this.submitted.set(false);

          return throwError(() => error);
        })
      );

      const loading$ = merge(
        timer(delay).pipe(
          map(() => true),
          takeUntil(source$)
        ),

        source$.pipe(map(() => false))
      );

      loading$
        .pipe(
          tap((state) => this.loading.set(state)),
          filter((state) => !state),
          tap(() => this._dialogRef.close(true)),
          take(1)
        )
        .subscribe();

      return;
    }

    this._dialogRef.close(true);
  }
}
